/**
 * Calendar Event Detail Dialog
 *
 * Displays event details when clicked.
 */

import { useNavigate } from 'react-router-dom';
import { CalendarDays, CheckSquare, User, Building2, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import type { CalendarEvent, CalendarEventType, ProjectType } from '../types';
import { formatEventDate, PROJECT_TYPE_LABELS, getEventColor } from '../utils';

interface CalendarEventPopoverProps {
  event: CalendarEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorEl: HTMLElement | null; // Kept for API compatibility but not used
}

export function CalendarEventPopover({
  event,
  open,
  onOpenChange,
}: CalendarEventPopoverProps) {
  const navigate = useNavigate();

  if (!event) return null;

  const eventType = event.type as CalendarEventType;
  const sourceType = event.sourceType as ProjectType | 'task';
  const colors = getEventColor(sourceType);

  // Get appropriate icon (project or task)
  const Icon = eventType === 'task' ? CheckSquare : CalendarDays;

  // Get type label
  const typeLabel = sourceType === 'task' ? 'Task' : PROJECT_TYPE_LABELS[sourceType as ProjectType] || sourceType;

  // Navigate to source entity
  const handleViewDetails = () => {
    onOpenChange(false);
    if (eventType === 'task') {
      navigate('/operations/tasks');
    } else {
      navigate('/operations/projects');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div
              className="p-2 rounded-md"
              style={{ backgroundColor: `${colors.bg}20` }}
            >
              <Icon className="h-4 w-4" style={{ color: colors.bg }} />
            </div>
            <span className="truncate">{event.title}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant="outline"
              className="text-xs"
              style={{
                borderColor: colors.border,
                color: colors.bg,
              }}
            >
              {typeLabel}
            </Badge>
            <Badge variant="secondary" className="text-xs capitalize">
              {event.status.replace('_', ' ')}
            </Badge>
            {event.priority && (
              <Badge variant="outline" className="text-xs capitalize">
                {event.priority}
              </Badge>
            )}
          </div>

          <Separator />

          {/* Details */}
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <CalendarDays className="h-4 w-4 flex-shrink-0" />
              <span>{formatEventDate(event.start)}</span>
            </div>

            {event.assigneeName && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <User className="h-4 w-4 flex-shrink-0" />
                <span>{event.assigneeName}</span>
              </div>
            )}

            {event.extendedProps.accountName && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Building2 className="h-4 w-4 flex-shrink-0" />
                <span>{event.extendedProps.accountName}</span>
              </div>
            )}

            {event.projectNumber && (
              <div className="text-muted-foreground text-xs">
                Project: {event.projectNumber}
              </div>
            )}

            {event.extendedProps.description && (
              <p className="text-muted-foreground text-xs line-clamp-3">
                {event.extendedProps.description}
              </p>
            )}
          </div>

          <Separator />

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button size="sm" onClick={handleViewDetails}>
              <ExternalLink className="h-3 w-3 mr-1" />
              View Details
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
